import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Save, Upload, ImageIcon } from "lucide-react";
import { useFileUpload } from "@/lib/uploadProgress";
import ProductDescriptionEditor from "@/components/ProductDescriptionEditor";

const EMPTY = { title: "", slug: "", excerpt: "", featured_image_url: "", category_id: "", body: "", is_published: true };

export default function AdminStoreBlogForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const uploadFile = useFileUpload();
  const [form, setForm] = useState(EMPTY);
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [ready, setReady] = useState(!id);

  useEffect(() => {
    api.get("/merchant/store-blog/categories").then(({ data }) => setCats(data)).catch(() => {});
    if (!id) return;
    api.get(`/merchant/store-blogs/${id}`).then(({ data }) => {
      setForm({
        title: data.title || "",
        slug: data.slug || "",
        excerpt: data.excerpt || "",
        featured_image_url: data.featured_image_url || "",
        category_id: data.category_id || "",
        body: data.body || "",
        is_published: data.is_published !== false,
      });
      setReady(true);
    }).catch((e) => { toast.error(extractErr(e)); nav("/admin/blogs"); });
  }, [id, nav]);

  const uploadImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "blog");
    setUploading(true);
    try {
      const data = await uploadFile("/merchant/media/upload", fd);
      setForm((f) => ({ ...f, featured_image_url: data.url }));
    } catch (e) { toast.error(extractErr(e)); } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        slug: form.slug || undefined,
        excerpt: form.excerpt,
        featured_image_url: form.featured_image_url || null,
        category_id: form.category_id || null,
        body: form.body,
        is_published: form.is_published,
      };
      if (id) await api.put(`/merchant/store-blogs/${id}`, payload);
      else await api.post("/merchant/store-blogs", payload);
      toast.success("Saved");
      nav("/admin/blogs");
    } catch (e) { toast.error(extractErr(e)); } finally { setSaving(false); }
  };

  if (!ready) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="max-w-3xl space-y-6">
      <Link to="/admin/blogs" className="text-sm text-slate-500 inline-flex items-center gap-1" data-testid="blog-form-back"><ArrowLeft className="w-4 h-4" /> Blogs</Link>
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">{id ? "Edit blog" : "New blog"}</h1>
        <p className="text-slate-500 text-sm mt-1">Featured image, title, and rich paragraphs with size, font, links, and images.</p>
      </div>

      <Card className="p-6 space-y-5">
        <div>
          <Label>Featured image</Label>
          <div className="mt-2 flex items-center gap-4">
            <div className="w-40 h-24 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0">
              {form.featured_image_url ? <img src={mediaUrl(form.featured_image_url)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-7 h-7 text-slate-300" />}
            </div>
            <label className="inline-flex items-center gap-1.5 text-sm text-blue-600 cursor-pointer" data-testid="blog-form-image">
              <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : (form.featured_image_url ? "Replace" : "Upload")}
              <input type="file" hidden accept="image/*" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} />
            </label>
            {form.featured_image_url && <button type="button" className="text-xs text-red-600" onClick={() => setForm({ ...form, featured_image_url: "" })}>Remove</button>}
          </div>
        </div>
        <div>
          <Label>Heading title</Label>
          <Input data-testid="blog-form-title" className="mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Slug (URL)</Label>
            <Input data-testid="blog-form-slug" className="mt-1 font-mono-nums" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto" />
          </div>
          <div>
            <Label>Category</Label>
            <select data-testid="blog-form-category" className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
              <option value="">Uncategorised</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
        <div>
          <Label>Short excerpt <span className="text-slate-400 font-normal">(homepage card)</span></Label>
          <Textarea data-testid="blog-form-excerpt" rows={2} className="mt-1" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
        </div>
        <div>
          <Label>Paragraphs</Label>
          <ProductDescriptionEditor rich value={form.body} onChange={(body) => setForm({ ...form, body })} testId="blog-form-body" />
        </div>
        <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
          <Label>Published</Label>
          <Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} data-testid="blog-form-published" />
        </div>
        <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="blog-form-save">
          <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save"}
        </Button>
      </Card>
    </div>
  );
}
